import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Shield, Search, User as UserIcon, RotateCw, Save, Users, Briefcase } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

const ROLE_LABELS = { employee: "Integrante", manager: "Gestor", hr: "RH", ceo: "CEO", admin: "Admin" };
const ELEVATABLE_ROLES = ["manager", "hr", "ceo", "admin"];

export default function PermissionsMatrix() {
  const { lang } = useI18n();
  const isPT = (lang || "pt") === "pt";
  const [mode, setMode] = useState("user"); // "user" | "profile"
  const [catalog, setCatalog] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobProfiles, setJobProfiles] = useState([]);
  const [q, setQ] = useState("");
  const [selectedUid, setSelectedUid] = useState(null);
  const [selectedJpId, setSelectedJpId] = useState(null);
  const [selection, setSelection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [localGrants, setLocalGrants] = useState(new Set());
  const [localDenies, setLocalDenies] = useState(new Set());
  const [localRoles, setLocalRoles] = useState(new Set());

  useEffect(() => {
    api.get("/permissions/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
    api.get("/permissions/job-profiles").then((r) => setJobProfiles(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (mode !== "user") return;
    if (!selectedUid) { setSelection(null); return; }
    api.get(`/permissions/users/${selectedUid}`)
      .then((r) => setSelection({ ...r.data, _kind: "user" }))
      .catch(() => setSelection(null));
  }, [mode, selectedUid]);

  useEffect(() => {
    if (mode !== "profile") return;
    if (!selectedJpId) { setSelection(null); return; }
    const jp = jobProfiles.find((p) => p.id === selectedJpId);
    if (!jp) return;
    setSelection({
      _kind: "profile", profile: jp, role_defaults: [],
      grants: jp.permission_grants || [], denies: [], effective_roles: [],
      effective: jp.permission_grants || [],
    });
  }, [mode, selectedJpId, jobProfiles]);

  useEffect(() => {
    if (!selection) { setLocalGrants(new Set()); setLocalDenies(new Set()); setLocalRoles(new Set()); return; }
    setLocalGrants(new Set(selection.grants || []));
    setLocalDenies(new Set(selection.denies || []));
    setLocalRoles(new Set(selection.effective_roles || []));
  }, [selection?._kind, selection?.user?.id, selection?.profile?.id]);

  const filteredUsers = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) => (u.name || "").toLowerCase().includes(term) || (u.email || "").toLowerCase().includes(term));
  }, [users, q]);
  const filteredProfiles = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return jobProfiles;
    return jobProfiles.filter((p) => (p.name || "").toLowerCase().includes(term) || (p.job_title || "").toLowerCase().includes(term));
  }, [jobProfiles, q]);

  if (!catalog) return <div className="text-[#94a3b8] text-sm">Carregando catálogo…</div>;

  const roleDefaults = new Set(selection?.role_defaults || []);
  const stateOf = (key) => {
    if (localDenies.has(key)) return "denied";
    if (localGrants.has(key)) return "granted";
    if (roleDefaults.has(key)) return "default";
    return "none";
  };
  const cycleState = (key) => {
    const s = stateOf(key);
    const g = new Set(localGrants); const d = new Set(localDenies);
    if (selection?._kind === "profile") {
      if (s === "granted") g.delete(key); else g.add(key);
    } else {
      if (s === "none") g.add(key);
      else if (s === "granted") { g.delete(key); d.add(key); }
      else if (s === "denied") d.delete(key);
      else if (s === "default") d.add(key);
    }
    setLocalGrants(g); setLocalDenies(d);
  };
  const toggleShadowRole = (role) => {
    const r = new Set(localRoles);
    if (r.has(role)) r.delete(role); else r.add(role);
    setLocalRoles(r);
  };

  const save = async () => {
    if (!selection) return;
    setSaving(true);
    try {
      if (selection._kind === "user") {
        const { data } = await api.patch(`/permissions/users/${selection.user.id}`,
          { grants: [...localGrants], denies: [...localDenies], effective_roles: [...localRoles] });
        toast.success("Permissões salvas");
        setSelection({ ...selection, grants: data.grants, denies: data.denies,
                       effective_roles: data.effective_roles, effective: data.effective });
      } else {
        const { data } = await api.patch(`/permissions/job-profiles/${selection.profile.id}`,
          { permission_grants: [...localGrants] });
        toast.success(`Template salvo — ${selection.profile.assigned_count} integrante(s) receberão automaticamente`);
        const list = await api.get("/permissions/job-profiles");
        setJobProfiles(list.data);
        setSelection((prev) => ({ ...prev, grants: data.permission_grants, effective: data.permission_grants,
                                  profile: { ...prev.profile, permission_grants: data.permission_grants } }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  const canSelect = selection !== null;
  const headerName = selection?._kind === "user" ? selection.user.name : selection?.profile?.name;
  const headerSubtitle = selection?._kind === "user"
    ? `${selection.user.email} · role base: ${ROLE_LABELS[selection.user.role] || selection.user.role}`
    : selection?.profile?.job_title
      ? `${selection.profile.job_title} · ${selection.profile.assigned_count} integrante(s) usam este template`
      : `${selection?.profile?.assigned_count || 0} integrante(s) usam este template`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6" data-testid="permissions-matrix">
      <div className="border border-[#e2e8f0] rounded-[12px] p-3 bg-white h-fit">
        <div className="grid grid-cols-2 gap-1 p-1 rounded-[8px] bg-[#f4f4f6] mb-3">
          <button data-testid="perms-mode-user" onClick={() => { setMode("user"); setSelectedJpId(null); }}
                  className={`text-[11.5px] font-medium py-1.5 rounded-[6px] flex items-center justify-center gap-1.5 transition-colors ${mode==="user" ? "bg-white text-[#1e293b] shadow-sm" : "text-[#64748b]"}`}>
            <Users size={12} /> Por Integrante
          </button>
          <button data-testid="perms-mode-profile" onClick={() => { setMode("profile"); setSelectedUid(null); }}
                  className={`text-[11.5px] font-medium py-1.5 rounded-[6px] flex items-center justify-center gap-1.5 transition-colors ${mode==="profile" ? "bg-white text-[#1e293b] shadow-sm" : "text-[#64748b]"}`}>
            <Briefcase size={12} /> Por Cargo
          </button>
        </div>
        <div className="flex items-center gap-2 px-2 pb-3">
          <Shield size={16} className="text-[#475569]" />
          <span className="text-[13px] font-semibold text-[#1e293b]">{mode === "user" ? "Permissões por integrante" : "Templates por cargo"}</span>
        </div>
        <div className="relative mb-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" data-testid="perms-search"
                 className="w-full pl-9 pr-3 py-2 text-[12px] rounded-[8px] border border-[#e2e8f0] bg-[#f9fafb] focus:outline-none focus:border-[#94a3b8]" />
        </div>
        <div className="max-h-[520px] overflow-y-auto flex flex-col gap-0.5 pr-1">
          {mode === "user" && filteredUsers.map((u) => (
            <button key={u.id} onClick={() => setSelectedUid(u.id)} data-testid={`perms-user-${u.id}`}
                    className={`text-left px-3 py-2 rounded-[8px] text-[12.5px] transition-colors ${selectedUid===u.id ? "bg-[#e0e7ff] text-[#1e293b]" : "hover:bg-[#f4f4f6]"}`}>
              <div className="font-medium truncate">{u.name}</div>
              <div className="text-[10.5px] text-[#94a3b8] flex items-center gap-1">
                <span>{ROLE_LABELS[u.role] || u.role}</span>
                {u.effective_roles?.length > 0 && <span className="text-[#0369a1]">+ {u.effective_roles.length}</span>}
              </div>
            </button>
          ))}
          {mode === "profile" && filteredProfiles.map((p) => (
            <button key={p.id} onClick={() => setSelectedJpId(p.id)} data-testid={`perms-profile-${p.id}`}
                    className={`text-left px-3 py-2 rounded-[8px] text-[12.5px] transition-colors ${selectedJpId===p.id ? "bg-[#dcfce7] text-[#1e293b]" : "hover:bg-[#f4f4f6]"}`}>
              <div className="font-medium truncate">{p.name}</div>
              <div className="text-[10.5px] text-[#94a3b8] flex items-center gap-2">
                <span>{p.assigned_count} pessoa(s)</span>
                {p.permission_grants?.length > 0 && <span className="text-[#16a34a]">· {p.permission_grants.length} perms</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="border border-[#e2e8f0] rounded-[12px] bg-white p-5 min-h-[400px]">
        {!canSelect ? (
          <div className="flex flex-col items-center justify-center h-full text-[#94a3b8] gap-2 py-10">
            <UserIcon size={28} strokeWidth={1.2} />
            <span className="text-[13px]">Selecione {mode === "user" ? "um integrante" : "um cargo"} à esquerda</span>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-[16px] font-semibold text-[#1e293b]">{headerName}</h3>
                <div className="text-[11.5px] text-[#94a3b8]">{headerSubtitle}</div>
                <div className="text-[10px] text-[#475569] mt-1">
                  {selection.effective.length} permissões · {selection.grants.length} grants
                  {selection._kind === "user" && ` · ${selection.denies.length} denies · ${selection.effective_roles.length} roles extras`}
                </div>
                {selection._kind === "profile" && (
                  <div className="text-[10.5px] mt-1 px-2 py-1 rounded bg-[#eff6ff] text-[#1e40af] inline-block">
                    Editar este template afeta <strong>{selection.profile.assigned_count}</strong> integrante(s) imediatamente
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setLocalGrants(new Set(selection.grants||[])); setLocalDenies(new Set(selection.denies||[])); setLocalRoles(new Set(selection.effective_roles||[])); }}
                        className="px-3 py-1.5 text-[12px] rounded-[8px] border border-[#e2e8f0] flex items-center gap-1.5 hover:bg-[#f4f4f6]" data-testid="perms-reset">
                  <RotateCw size={13} /> Reverter
                </button>
                <button onClick={save} disabled={saving}
                        className="px-3 py-1.5 text-[12px] rounded-[8px] bg-[#1e293b] text-white flex items-center gap-1.5 hover:bg-[#0f172a] disabled:opacity-50" data-testid="perms-save">
                  <Save size={13} /> {saving ? "Salvando…" : "Salvar"}
                </button>
              </div>
            </div>

            {selection._kind === "user" && (
              <div className="mb-6 p-3 rounded-[10px] bg-[#f9fafb] border border-[#e2e8f0]">
                <div className="text-[11px] uppercase tracking-wider text-[#475569] font-semibold mb-2">Roles Extras (escalação)</div>
                <div className="text-[11px] text-[#94a3b8] mb-3">Concede as permissões-base de um role adicional sem trocar o role principal.</div>
                <div className="flex flex-wrap gap-2">
                  {ELEVATABLE_ROLES.filter(r => r !== selection.user.role).map((r) => (
                    <label key={r} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-[12px] cursor-pointer transition-colors ${localRoles.has(r) ? "bg-[#dbeafe] border-[#93c5fd] text-[#1e40af]" : "border-[#e2e8f0] hover:bg-[#f4f4f6]"}`}
                           data-testid={`perms-shadow-${r}`}>
                      <input type="checkbox" checked={localRoles.has(r)} onChange={() => toggleShadowRole(r)} className="hidden" />
                      <span className="font-medium">{ROLE_LABELS[r]}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="text-[11px] uppercase tracking-wider text-[#475569] font-semibold mb-3">
              {selection._kind === "user" ? "Permissões Individuais" : "Permissões do Template"}
            </div>
            <div className="text-[11px] text-[#94a3b8] mb-3">
              {selection._kind === "user"
                ? <>Clique: 1x add (verde), 2x deny (vermelho), 3x volta ao padrão. <span className="ml-2 inline-flex gap-2 items-center"><span className="w-3 h-3 rounded-sm bg-[#94a3b8]" /> herdado<span className="w-3 h-3 rounded-sm bg-[#22c55e]" /> add<span className="w-3 h-3 rounded-sm bg-[#ef4444]" /> deny</span></>
                : <>Clique para incluir/remover permissão do template. Todo integrante nesse cargo herda automaticamente.</>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto pr-1">
              {Object.entries(catalog.modules).map(([mod, perms]) => (
                <div key={mod} className="border border-[#e2e8f0] rounded-[10px] p-3">
                  <div className="text-[11.5px] font-semibold text-[#1e293b] mb-2 uppercase tracking-wide">{mod.replace(/_/g," ")}</div>
                  <div className="flex flex-col gap-1">
                    {perms.map((p) => {
                      const s = stateOf(p.key);
                      const color = s === "granted" ? "bg-[#22c55e] border-[#16a34a]" :
                                    s === "denied"  ? "bg-[#ef4444] border-[#dc2626]" :
                                    s === "default" ? "bg-[#94a3b8] border-[#64748b] opacity-70" :
                                    "bg-transparent border-[#cbd5e1]";
                      return (
                        <button key={p.key} onClick={() => cycleState(p.key)} data-testid={`perm-${p.key}`}
                                className="flex items-center gap-2 text-left py-1 hover:bg-[#f9fafb] rounded transition-colors">
                          <span className={`w-4 h-4 border-2 rounded-sm shrink-0 ${color}`} />
                          <div className="min-w-0">
                            <div className="text-[12px] text-[#1e293b] truncate">{isPT ? p.label_pt : p.label_en}</div>
                            <div className="text-[10px] text-[#94a3b8] truncate">{p.key}{p.sensitive && <span className="ml-1 text-[#b45309]">⚠</span>}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
